import { differenceInHours, parseISO } from 'date-fns';
import { Scan } from '../../../../../services/entities/impl/blfApi/schemas/scan';
import { UploadDetail } from '../../../../../services/entities/impl/blfApi/schemas/uploadSummary';
import {
  ValidationMessage,
  ValidationResult,
} from '../../../utils/validation/validateSchema';

/**
 * Checks the status of scan processing based on upload completion time
 *
 * @param uploadDetail - The upload detail containing completion information
 * @param scans - Array of scans to check if any exist
 * @returns ValidationResult object with any warnings or errors
 */
export const checkScanProcessingStatus = (
  uploadDetail: UploadDetail | undefined,
  scans: Scan[] | undefined
): ValidationResult => {
  // Default result with no issues
  const validResult: ValidationResult = { valid: true, messages: [] };

  // Skip checks if criteria not met
  if (
    !uploadDetail ||
    !uploadDetail.latestFileUploadedAt ||
    uploadDetail.percentUploaded < 100 ||
    (scans && scans.length > 0)
  ) {
    return validResult;
  }

  try {
    const latestUploadTime = parseISO(uploadDetail.latestFileUploadedAt);
    const currentTime = new Date();
    const hoursSinceUpload = differenceInHours(currentTime, latestUploadTime);

    const message: ValidationMessage = {
      path: 'scans',
      severity: 'error',
      message: '',
    };

    if (hoursSinceUpload >= 48) {
      message.severity = 'error';
      message.message = `Upload completed ${hoursSinceUpload} hours ago, but no scans have appeared. The processing pipeline may have encountered an issue.`;

      return {
        valid: false,
        messages: [message],
      };
    } else if (hoursSinceUpload >= 24) {
      message.severity = 'warning';
      message.message = `Upload completed ${hoursSinceUpload} hours ago, but scans are still being processed. This is taking longer than expected.`;

      return {
        valid: false,
        messages: [message],
      };
    }
  } catch (e) {
    console.error('Error calculating time since upload completion', e);
  }

  return validResult;
};
